import styled from "styled-components";
import { useVictimsAndKillers } from "../../../hooks/useVictimsAndKillers";
import { useCustomMarkers } from "../../../hooks/useCustomMarkers";

const SelectedFiltersList = styled.div`
  position: absolute;
  top: 57px;
  right: 16px;
  z-index: 1000;
  width: 283px;
  max-width: calc(100% - 32px);
  pointer-events: auto;
  background: #393e46;
  border: 1px solid #222831;
  border-radius: 8px;
  padding: 8px 10px;
  max-height: calc(100vh - 104px);
  overflow-y: auto;
  @media (max-width: 768px) {
    top: 100px;
    max-height: 30vh;
  }
  &::-webkit-scrollbar-track {
    background: #393e46;
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: #222831;
    border-radius: 5px;
  }
`;

const SelectedFiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #eeeeee;
  font-size: 13px;
  line-height: 1.4;

  &:not(:last-child) {
    margin-bottom: 6px;
  }
`;

const SelectedFilterName = styled.span<{ $hidden: boolean }>`
  text-decoration: ${({ $hidden }) => ($hidden ? "line-through" : "none")};
`;

const ColorSwatch = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  border: 1px solid rgba(255, 255, 255, 0.35);
  flex: 0 0 12px;
`;

export default function SelectedFiltersSummary() {
  const { filters, serialKillerById } = useVictimsAndKillers();
  const { groups } = useCustomMarkers();

  const selectedKillerRows = filters.filter(
    (row) => row.type !== "serialkiller" || !!row.killerId,
  );

  if (selectedKillerRows.length === 0 && groups.length === 0) return null;

  const killerEntries = selectedKillerRows
    .map((row) => {
      const killer = row.killerId ? serialKillerById[row.killerId] : null;
      const location = [row.country, row.region]
        .filter((value): value is string => Boolean(value))
        .join(", ");
      const locationSuffix = location ? ` (${location})` : "";
      const killerName =
        killer?.name ??
        (row.type === "unsolved"
          ? `Unsolved Murders${locationSuffix}`
          : row.type === "missing"
            ? `Missing Persons${locationSuffix}`
            : (row.killerId ?? "Unknown"));
      return {
        key: row.id,
        color: row.color,
        hidden: row.visible === false,
        label: killerName,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const groupEntries = [...groups]
    .map((g) => ({
      key: `mg-${g.id}`,
      color: g.color,
      hidden: g.visible === false,
      label: g.name || "Marker group",
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <SelectedFiltersList>
      {killerEntries.map((entry) => (
        <SelectedFiltersRow key={entry.key}>
          <ColorSwatch $color={entry.color} aria-hidden />
          <SelectedFilterName $hidden={entry.hidden}>
            {entry.label}
          </SelectedFilterName>
        </SelectedFiltersRow>
      ))}
      {groupEntries.map((entry) => (
        <SelectedFiltersRow key={entry.key}>
          <ColorSwatch $color={entry.color} aria-hidden />
          <SelectedFilterName $hidden={entry.hidden}>
            {entry.label}
          </SelectedFilterName>
        </SelectedFiltersRow>
      ))}
    </SelectedFiltersList>
  );
}
